import React, { useEffect, useState, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { X, Shield, FileText, Users, HelpCircle, Coffee, User, LogOut, LogIn, Martini, Eye, Camera, UserPlus, QrCode } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { getAPIRoot, getCachedAccessToken } from '../services/api';
import { useUnified } from '../contexts/UnifiedContext';
import { getAccountInitials } from '../utils/photoResolver';

const SlideOutMenu = ({ isOpen, onClose }) => {
  const navigate = useNavigate();
  const { user, isAuthenticated, signOut } = useAuth();
  const { unified } = useUnified();
  const dialogTitleId = React.useId();
  const firstItemRef = useRef(null);
  const panelRef = useRef(null);
  const backdropRef = useRef(null);
  const [shouldRender, setShouldRender] = useState(isOpen);
  const [inviteCredits, setInviteCredits] = useState(null);
  const API = getAPIRoot();

  useEffect(() => {
    if (!isOpen) {
      const t = window.setTimeout(() => setShouldRender(false), 280);
      return () => window.clearTimeout(t);
    } else {
      setShouldRender(true);
    }
    return undefined;
  }, [isOpen]);

  // Fetch invite credits when menu opens
  useEffect(() => {
    if (!isOpen || !isAuthenticated) return;
    let cancelled = false;
    const token2 = user?.access_token || getCachedAccessToken();
    const h = token2 ? { Authorization: `Bearer ${token2}` } : {};
    fetch(`${API}/invite/my-code`, {
      credentials: 'include',
      headers: h,
    })
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => {
        if (cancelled || !d) return;
        setInviteCredits({
          remaining: d.credits_remaining ?? 0,
          total: d.credits_total ?? 3,
        });
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [isOpen, isAuthenticated, API, user?.access_token]);

  // ── Menu action helpers ────────────────────────────────────────────

  const go = (path) => {
    onClose();
    setTimeout(() => navigate(path), 80);
  };

  const openInvite = () => {
    onClose();
    setTimeout(() => {
      window.dispatchEvent(new CustomEvent('openInviteFriends'));
    }, 80);
  };

  // ── Menu items ─────────────────────────────────────────────────────

  const coreItems = [
    { icon: Martini, label: 'Bars', action: () => go('/stag-venues') },
    { icon: Eye, label: 'Radar', action: () => go('/radar') },
    { icon: Users, label: 'Signals', action: () => go('/matches') },
    { icon: Coffee, label: 'Work', action: () => go('/work') },
  ];

  const growItems = isAuthenticated ? [
    {
      icon: UserPlus,
      label: 'Invite friends',
      badge: inviteCredits ? `${inviteCredits.remaining}/${inviteCredits.total}` : null,
      action: openInvite,
    },
  ] : [];

  const profileItems = isAuthenticated ? [
    { icon: QrCode, label: 'STAGSTER Passport', action: () => { onClose(); setTimeout(() => window.dispatchEvent(new CustomEvent('openPassportModal')), 80); } },
    { icon: Camera, label: 'Edit profile', action: () => go('/profile/edit') },
    { icon: Shield, label: 'Safety', action: () => go('/safety') },
    { icon: HelpCircle, label: 'Guidelines', action: () => go('/safety-guidelines') },
  ] : [];

  const aboutItems = [
    { icon: FileText, label: 'About', action: () => go('/about') },
    { icon: FileText, label: 'FAQ', action: () => go('/faq') },
    { icon: FileText, label: 'Privacy', action: () => go('/privacy') },
    { icon: FileText, label: 'Terms', action: () => go('/terms') },
  ];

  const menuSections = [
    { section: 'Discover', items: coreItems },
    ...(growItems.length ? [{ section: 'Grow', items: growItems }] : []),
    ...(profileItems.length ? [{ section: 'Profile', items: profileItems }] : []),
    { section: 'About', items: aboutItems },
  ];

  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget) onClose();
  };

  // Body scroll lock
  useEffect(() => {
    if (isOpen) { document.body.style.overflow = 'hidden'; }
    else { document.body.style.overflow = ''; }
    return () => { document.body.style.overflow = ''; };
  }, [isOpen]);

  // Keyboard & focus trap
  useEffect(() => {
    if (!isOpen) return;
    const focusable = panelRef.current?.querySelectorAll('button, [href], [tabindex]:not([tabindex="-1"])');
    const first = firstItemRef.current || focusable?.[0];
    const last = focusable?.[focusable.length - 1];
    first?.focus();
    const handler = (e) => {
      if (e.key === 'Escape') { e.preventDefault(); onClose(); return; }
      if (e.key !== 'Tab' || !focusable?.length) return;
      if (e.shiftKey) {
        if (document.activeElement === first) { e.preventDefault(); (last || first)?.focus(); }
      } else if (document.activeElement === last) { e.preventDefault(); (first || last)?.focus(); }
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [isOpen, onClose]);

  // Swipe-to-close
  const dragRef = useRef({ startX: 0, currentX: 0, dragging: false });
  const getPanelWidth = useCallback(() => panelRef.current?.offsetWidth || 320, []);

  const onTouchStart = useCallback((e) => {
    const d = dragRef.current;
    d.startX = e.touches[0].clientX;
    d.currentX = d.startX;
    d.dragging = true;
    if (panelRef.current) panelRef.current.style.transition = 'none';
    if (backdropRef.current) backdropRef.current.style.transition = 'none';
  }, []);

  const onTouchMove = useCallback((e) => {
    const d = dragRef.current;
    if (!d.dragging) return;
    d.currentX = e.touches[0].clientX;
    const dx = Math.min(0, d.currentX - d.startX);
    if (panelRef.current) panelRef.current.style.transform = `translate3d(${dx}px, 0, 0)`;
    if (backdropRef.current) backdropRef.current.style.opacity = String(Math.max(0, 1 + dx / getPanelWidth()));
  }, [getPanelWidth]);

  const onTouchEnd = useCallback(() => {
    const d = dragRef.current;
    if (!d.dragging) return;
    d.dragging = false;
    const dx = d.currentX - d.startX;
    const shouldClose = dx < -30 || dx < -(getPanelWidth() * 0.3);
    const ease = 'cubic-bezier(0.32, 0.72, 0, 1)';
    if (panelRef.current) panelRef.current.style.transition = `transform 0.35s ${ease}`;
    if (backdropRef.current) backdropRef.current.style.transition = `opacity 0.35s ${ease}`;
    if (shouldClose) {
      if (panelRef.current) panelRef.current.style.transform = 'translate3d(-100%, 0, 0)';
      if (backdropRef.current) backdropRef.current.style.opacity = '0';
      setTimeout(onClose, 350);
    } else {
      if (panelRef.current) panelRef.current.style.transform = 'translate3d(0, 0, 0)';
      if (backdropRef.current) backdropRef.current.style.opacity = '1';
    }
  }, [onClose, getPanelWidth]);

  useEffect(() => {
    if (isOpen && panelRef.current) { panelRef.current.style.transform = ''; panelRef.current.style.transition = ''; }
    if (isOpen && backdropRef.current) { backdropRef.current.style.opacity = ''; backdropRef.current.style.transition = ''; }
  }, [isOpen]);

  if (!shouldRender) return null;

  const ease = 'cubic-bezier(0.32, 0.72, 0, 1)';
  const profileSubtitle = unified?.user?.city || 'Your profile';
  const avatarLabel = getAccountInitials(user, unified?.user ?? null, unified?.friday_swipe ?? null);

  return (
    <>
      {/* Backdrop */}
      <div
        ref={backdropRef}
        className={`fixed inset-0 bg-black/60 z-[80] ${isOpen ? 'opacity-100' : 'opacity-0 pointer-events-none'}`}
        style={{ transition: `opacity 0.35s ${ease}`, willChange: 'opacity' }}
        onClick={handleBackdropClick}
        aria-hidden="true"
      />

      {/* Menu Panel */}
      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={dialogTitleId}
        className={`fixed top-0 left-0 h-full w-[86vw] max-w-[340px] z-[90] flex flex-col bg-black ${isOpen ? 'translate-x-0' : '-translate-x-full'}`}
        style={{ transition: `transform 0.35s ${ease}`, willChange: 'transform', boxShadow: '20px 0 60px rgba(0,0,0,0.85)' }}
        onTouchStart={onTouchStart}
        onTouchMove={onTouchMove}
        onTouchEnd={onTouchEnd}
      >
        {/* Close */}
        <div className="flex shrink-0 items-center justify-end px-4 pt-3 pb-1">
          <h2 id={dialogTitleId} className="sr-only">Menu</h2>
          <button onClick={onClose} className="rounded-full p-2 text-white/40 hover:text-white/70 transition-colors" type="button" aria-label="Close menu">
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Scrollable content */}
        <div className="flex-1 overflow-y-auto scrollbar-thin px-4 pb-6">
          {/* Auth / Profile card */}
          <div className="pb-5 border-b border-white/[0.06]">
            {isAuthenticated ? (
              <div className="flex items-start gap-3">
                <button type="button" onClick={() => go('/profile')} className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-white/[0.06] text-white">
                  {avatarLabel ? (
                    <span className="text-xs font-semibold uppercase tracking-[0.08em] text-white/85">{avatarLabel}</span>
                  ) : (
                    <User className="h-5 w-5 text-white/70" />
                  )}
                </button>
                <div className="min-w-0 flex-1 pt-0.5">
                  <p className="truncate text-[15px] font-semibold text-white/92">
                    {user?.user_metadata?.full_name || user?.user_metadata?.name || user?.email || 'STAGSTER'}
                  </p>
                  <p className="truncate text-xs text-white/50 mt-0.5">{profileSubtitle}</p>
                  <div className="mt-2 flex gap-2">
                    <button type="button" onClick={() => go('/profile')} className="rounded-full bg-white/[0.06] px-3 py-1 text-[11px] font-medium text-white/70 hover:bg-white/[0.1]">View</button>
                    <button type="button" onClick={() => go('/profile/edit')} className="rounded-full bg-white/[0.06] px-3 py-1 text-[11px] font-medium text-white/70 hover:bg-white/[0.1]">Edit</button>
                  </div>
                </div>
              </div>
            ) : (
              <div>
                <p className="text-[15px] font-semibold text-white/90">Join STAGSTER</p>
                <p className="mt-1 text-[13px] text-white/50">Save places, open Radar, and find your people.</p>
                <button type="button" onClick={() => { try { window.dispatchEvent(new CustomEvent('openAuthPrompt', { detail: { context: 'explore' } })); } catch(_) {} onClose(); }}
                  className="mt-3 flex w-full items-center gap-3 rounded-xl bg-white/[0.06] px-4 py-3 text-white hover:bg-white/[0.09] transition-colors">
                  <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-white text-black">
                    <LogIn className="h-4 w-4" />
                  </span>
                  <span className="flex flex-col">
                    <span className="text-[10px] uppercase tracking-[0.06em] text-white/50">Go out freely</span>
                    <span className="text-sm font-semibold">Sign in with Google</span>
                  </span>
                </button>
              </div>
            )}
          </div>

          {/* Navigation sections */}
          {menuSections.map((section, idx) => (
            <div key={idx} className="pt-5">
              <h3 className="mb-2 px-1 text-[10px] font-bold uppercase tracking-[0.18em] text-white/40">{section.section}</h3>
              <div className="space-y-0.5">
                {section.items.map((item, i) => {
                  const Icon = item.icon;
                  return (
                    <button
                      key={i}
                      ref={idx === 0 && i === 0 ? firstItemRef : undefined}
                      type="button"
                      onClick={item.action}
                      className={`w-full flex items-center gap-3 rounded-xl px-3 py-2.5 text-left text-[14px] font-medium text-white/85 hover:bg-white/[0.05] active:bg-white/[0.08] transition-colors ${item.isSOS ? 'ring-1 ring-[#ffcb84]/20' : ''}`}
                    >
                      <Icon className="w-[18px] h-[18px] shrink-0 text-white/45" strokeWidth={1.7} />
                      <span className="min-w-0 truncate">{item.label}</span>
                      {item.badge && (
                        <span className={`ml-auto shrink-0 rounded-full px-2 py-0.5 text-[10px] font-bold tabular-nums ${
                          parseInt(String(item.badge).split('/')[0]) > 0
                            ? 'bg-[#ffcb84]/20 text-[#ffcb84]'
                            : 'bg-white/[0.06] text-white/40'
                        }`}>
                          {item.badge}
                        </span>
                      )}
                    </button>
                  );
                })}
              </div>
            </div>
          ))}
        </div>

        {/* Sign out */}
        {isAuthenticated && (
          <div className="shrink-0 border-t border-white/[0.06] px-4 py-3">
            <button onClick={() => { onClose(); setTimeout(() => { signOut(); navigate('/'); }, 80); }}
              className="flex w-full items-center justify-center gap-2 rounded-xl bg-white/[0.04] px-3 py-2.5 text-[13px] font-medium text-white/50 hover:bg-white/[0.07] hover:text-white/70 transition-colors" type="button">
              <LogOut className="w-4 h-4" />
              Sign out
            </button>
          </div>
        )}
      </div>
    </>
  );
};

export default SlideOutMenu;